import React, { useMemo, useState } from 'react';
import { BsChevronDown, BsChevronUp, BsStar, BsStarFill, BsThreeDots, BsTrash } from 'react-icons/bs';
import { useDispatch, useSelector } from 'react-redux';
import { Box, HStack, Icon, IconButton, Text } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import { CypressIds } from '../../../../../../../../../../constants/cypressIds';
import { hideTray, showTray } from '../../../../../../../../../../redux/modules/tray';
import {
  useCreateQuestionCategoryFavorite,
  useCreateQuestionFavorite, useDeleteQuestion, useDeleteQuestionCategory, useEditQuestion, useEditQuestionCategory,
  useRemoveQuestionCategoryFavorite,
  useRemoveQuestionFavorite
} from '../../../../../../../../../../services/question-service';
import colors from '../../../../../../../../../../styles/theme/foundation/colors';
import RequirementTemplateBlueprintType from '../../../../../../../../../../types/RequirementTemplateBlueprintType';
import IconButtonMenu from '../../../../../../../../../IconButtonMenu';
import QuestionLinkTray from '../QuestionLinkTray';

import { customQuestionCategoryOptions, customQuestionOptions } from './options';

const QuestionCategoryButton = ({ blueprint, companyId, questionCategory, searchTerm, currentRequirementTemplateBlocks, ...props }) => {
  const dispatch = useDispatch();
  const [dropDownOpen, setDropDownOpen] = useState(false);
  const { mutate: createQuestionFavorite } = useCreateQuestionFavorite({ companyId });
  const { mutate: removeQuestionFavorite } = useRemoveQuestionFavorite({ companyId });
  const { mutate: createQuestionCategoryFavorite } = useCreateQuestionCategoryFavorite({ companyId });
  const { mutate: removeQuestionCategoryFavorite } = useRemoveQuestionCategoryFavorite({ companyId });
  const { mutate: editQuestion } = useEditQuestion({ companyId, blueprint });
  const { mutate: deleteQuestion } = useDeleteQuestion({ companyId });
  const { mutateAsync: editQuestionCategory } = useEditQuestionCategory({ companyId });
  const { mutateAsync: deleteQuestionCategory } = useDeleteQuestionCategory({ companyId });

  const blueprintName = useSelector(blueprint.selectBlueprintName);

  const showEditAndDeleteButtons = useMemo(() => blueprintName === 'globalRequirementTemplateBuilder', [blueprintName]);

  const onFavoriteCategoryButtonClick = (categoryId, isFavorite) => {
    isFavorite
      ? removeQuestionCategoryFavorite({ categoryId, searchTerm })
      : createQuestionCategoryFavorite({ categoryId, searchTerm });
  };

  const onFavoriteQuestionButtonClick = (categoryId, questionId, isFavorite) => {
    isFavorite
      ? removeQuestionFavorite({ categoryId, questionId, searchTerm })
      : createQuestionFavorite({ categoryId, questionId, searchTerm });
  };

  const onQuestionClick = question => {
    dispatch(showTray(
      () => (
        <QuestionLinkTray
          blueprint={blueprint}
          currentRequirementTemplateBlocks={currentRequirementTemplateBlocks}
          question={question}
        />
      )
    ));
  };

  const categoryInUse = useMemo(() => questionCategory.questions.find(q => q.requirementTemplateQuestions?.length > 0), [questionCategory]);

  return (
    <>
      <Box
        _hover={{
          color: colors.primaryScale['600'],
          background: 'white'
        }}
        background="inherit"
        borderBottom="1px solid"
        borderBottomColor={colors.greyScale['200']}
        borderTop="1px solid"
        borderTopColor={colors.greyScale['200']}
        color={colors.greyScale['900']}
        cursor="pointer"
        key={questionCategory.id}
        minHeight="56px"
        padding="0 1rem 0 1rem"
        width="100%"
        {...props}
      >
        <HStack
          _hover={{
            bgClip: 'text',
            bgGradient: colors.gradient.aiPrimary
          }}
          justifyContent="space-between"
          lineHeight="20px"
          minHeight="calc(56px - 1rem)"
          my={2}
          onClick={e => {
            e.stopPropagation();
            e.preventDefault();
            setDropDownOpen(!dropDownOpen);
          }}
        >
          <HStack>
            <IconButton
              aria-label="favorite-question-category"
              data-cy={CypressIds.questionCategoryFavoriteButton(questionCategory.id)}
              data-favorite={questionCategory.favorite}
              icon={(
                <Icon
                  as={questionCategory.favorite ? BsStarFill : BsStar}
                  color={questionCategory.favorite ? '#FFDD00' : colors.greyScale['300']}
                  size="sm"
                />
              )}
              mr="14px"
              size="sm"
              variant="textPrimary"
              onClick={e => {
                e.stopPropagation();
                e.preventDefault();
                onFavoriteCategoryButtonClick(questionCategory.id, questionCategory.favorite);
              }}
            />
            <Text
              bgClip={categoryInUse ? 'text' : ''}
              bgGradient={categoryInUse ? colors.gradient.aiPrimary : ''}
              data-cy={CypressIds.questionCategoryButton(questionCategory.id)}
              sx={dropDownOpen && !!questionCategory.questions.length ? {
                bgClip: 'text',
                bgGradient: colors.gradient.aiPrimary
              } : {}}
              textAlign="left"
              textStyle="bodyMd500"
            >
              {questionCategory.name}
              <span
                style={{
                  color: colors.greyScale['500'],
                  fontSize: '12px'
                }}
              >
                {`${questionCategory.questions.length === 0 ? '  – empty' : ''}`}
              </span>
            </Text>
          </HStack>
          {(showEditAndDeleteButtons && ((questionCategory.companyId && !dropDownOpen) || !questionCategory.questions.length))
            ? (
              <IconButtonMenu
                blueprint={blueprint}
                cypressId={CypressIds.questionCategoryOptionsButton(questionCategory.id)}
                iconElement={<Icon as={BsThreeDots} width="20px" />}
                id={questionCategory.id}
                menuOptions={customQuestionCategoryOptions(questionCategory, editQuestionCategory, deleteQuestionCategory)}
                mr="14px"
                size="xs"
                width="32px"
              />
            ) : (
              <IconButton
                aria-label="question-category-dropdown-icon"
                icon={(
                  <Icon
                    as={dropDownOpen ? BsChevronDown : BsChevronUp}
                    color={colors.greyScale['500']}
                    size="sm"
                  />
                )}
                mr="14px"
                size="sm"
                variant="textPrimary"
              />
            )}
        </HStack>
      </Box>

      {dropDownOpen && questionCategory.questions.map(question => {
        const inUse = question.requirementTemplateQuestions?.length > 0;
        const starColor = () => {
          if (question.favorite) {
            return '#FFDD00';
          }

          return inUse ? colors.greyScale['500'] : colors.greyScale['300'];
        };

        return (
          <Box
            _hover={{ color: colors.gradient.aiPrimary }}
            background="inherit"
            borderBottom="1px solid"
            borderBottomColor={colors.greyScale['200']}
            borderTop="1px solid"
            borderTopColor={colors.greyScale['200']}
            color={colors.greyScale['900']}
            cursor="pointer"
            key={questionCategory.id}
            minHeight="56px"
            padding="0 1rem 0 1rem"
            width="100%"
            {...props}
          >
            <HStack justifyContent="space-between">
              <HStack
                _hover={{ color: colors.gradient.aiPrimary, bgClip: 'text', bgGradient: colors.gradient.aiPrimary }}
                key={question.id}
                lineHeight="20px"
                my={2}
                onClick={() => onQuestionClick(question)}
              >
                <IconButton
                  aria-label="favorite-question"
                  data-cy={CypressIds.questionFavoriteButton(question.id)}
                  data-favorite={question.favorite}
                  icon={(
                    <Icon
                      as={question.favorite ? BsStarFill : BsStar}
                      color={starColor()}
                      size="sm"
                    />
                  )}
                  mr="14px"
                  size="sm"
                  variant="textPrimary"
                  onClick={e => {
                    e.stopPropagation();
                    e.preventDefault();
                    onFavoriteQuestionButtonClick(questionCategory.id, question.id, question.favorite);
                  }}
                />
                <Text
                  bgClip={inUse ? 'text' : ''}
                  bgGradient={inUse ? colors.gradient.aiPrimary : ''}
                  data-cy={CypressIds.questionButton(question.id)}
                  data-selected={inUse}
                  textAlign="left"
                  textStyle="bodySm"
                >
                  {question.question}
                </Text>
              </HStack>

              {(showEditAndDeleteButtons && !!question.companyId) && (
                <IconButtonMenu
                  blueprint={blueprint}
                  cypressId={CypressIds.questionOptionsButton(question.id)}
                  iconElement={<Icon as={BsThreeDots} width="20px" />}
                  id={question.id}
                  menuOptions={customQuestionOptions(question, editQuestion, deleteQuestion)}
                  size="xs"
                  width="auto"
                />
              )}
            </HStack>
          </Box>
        );
      })}
    </>
  );
};

QuestionCategoryButton.propTypes = {
  blueprint: RequirementTemplateBlueprintType.isRequired,
  companyId: PropTypes.number.isRequired,
  searchTerm: PropTypes.string.isRequired,
  currentRequirementTemplateBlocks: PropTypes.array,
  questionCategory: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    favorite: PropTypes.bool,
    questions: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      question: PropTypes.string,
      favorite: PropTypes.bool
    }))
  }).isRequired
};

export default QuestionCategoryButton;
